import { Axios, AxiosRequestConfig } from 'axios';
import apiClient from 'services/api/axios';
import {
  IAsgListResDtoResponse,
  IAsgMemberListResDtoResponse,
  IAsgMemberResDtoResponse,
  IAsgResDtoResponse,
  IAsgRollingUpdateResDtoResponse,
  IAsgScalingPolicyResDtoResponse,
  IAsgScheduleListResDtoResponse,
  IAsgUserDataResDtoResponse,
  IAsgScheduleResDtoResponse,
  IAsgScalingPolicyReqDto,
  IAsgScheduleReqDto,
  ICreateAsgReqDto,
  ISetAsgSizeReqDto,
  IUpdateAsgReqDto,
  IUpdateAsgScheduleReqDto,
} from 'components/AutoscalingGroups/types/api-bodies.types';

const requestConfig = (regionId: number, projectTag: string): AxiosRequestConfig => ({
  params: { projectTag },
  headers: { 'X-Region-Id': regionId.toString() },
});

class AutoscalingGroupsService {
  api: Axios;

  constructor(api: Axios) {
    this.api = api;
  }

  createAutoscalingGroups(dto: ICreateAsgReqDto, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      'v2/auto-scaling-groups',
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  getAsgs(regionId: number, projectTag: string) {
    return this.api.get<IAsgListResDtoResponse>(
      'v2/auto-scaling-groups',
      requestConfig(regionId, projectTag),
    );
  }

  getAsgById(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}`,
      requestConfig(regionId, projectTag),
    );
  }

  getAsgUserData(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgUserDataResDtoResponse>(
      `v2/auto-scaling-groups/${id}/user-data`,
      requestConfig(regionId, projectTag),
    );
  }

  updateAsg(id: number, dto: IUpdateAsgReqDto, regionId: number, projectTag: string) {
    return this.api.patch<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}`,
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  deleteAsg(id: number, regionId: number, projectTag: string) {
    return this.api.delete(
      `v2/auto-scaling-groups/${id}`,
      requestConfig(regionId, projectTag),
    );
  }

  startAsg(id: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/start`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  stopAsg(id: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/stop`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  pauseAsg(id: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/pause`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  resumeAsg(id: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/resume`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  startRollingUpdate(id: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/rolling-update`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  // Отдаёт 809, пока обновление ни разу не запускалось: дёргать только для
  // группы в статусе ROLLING_UPDATE или сразу после своего POST.
  getRollingUpdate(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgRollingUpdateResDtoResponse>(
      `v2/auto-scaling-groups/${id}/rolling-update`,
      requestConfig(regionId, projectTag),
    );
  }

  setSize(id: number, dto: ISetAsgSizeReqDto, regionId: number, projectTag: string) {
    return this.api.post<IAsgResDtoResponse>(
      `v2/auto-scaling-groups/${id}/size`,
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  getScalingPolicy(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgScalingPolicyResDtoResponse>(
      `v2/auto-scaling-groups/${id}/scaling-policy`,
      requestConfig(regionId, projectTag),
    );
  }

  setScalingPolicy(
    id: number,
    dto: IAsgScalingPolicyReqDto,
    regionId: number,
    projectTag: string,
  ) {
    return this.api.put<IAsgScalingPolicyResDtoResponse>(
      `v2/auto-scaling-groups/${id}/scaling-policy`,
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  deleteScalingPolicy(id: number, regionId: number, projectTag: string) {
    return this.api.delete(
      `v2/auto-scaling-groups/${id}/scaling-policy`,
      requestConfig(regionId, projectTag),
    );
  }

  getSchedules(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgScheduleListResDtoResponse>(
      `v2/auto-scaling-groups/${id}/schedules`,
      requestConfig(regionId, projectTag),
    );
  }

  createSchedule(id: number, dto: IAsgScheduleReqDto, regionId: number, projectTag: string) {
    return this.api.post<IAsgScheduleResDtoResponse>(
      `v2/auto-scaling-groups/${id}/schedules`,
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  updateSchedule(
    id: number,
    scheduleId: number,
    dto: IUpdateAsgScheduleReqDto,
    regionId: number,
    projectTag: string,
  ) {
    return this.api.patch<IAsgScheduleResDtoResponse>(
      `v2/auto-scaling-groups/${id}/schedules/${scheduleId}`,
      dto,
      requestConfig(regionId, projectTag),
    );
  }

  deleteSchedule(id: number, scheduleId: number, regionId: number, projectTag: string) {
    return this.api.delete(
      `v2/auto-scaling-groups/${id}/schedules/${scheduleId}`,
      requestConfig(regionId, projectTag),
    );
  }

  getMembers(id: number, regionId: number, projectTag: string) {
    return this.api.get<IAsgMemberListResDtoResponse>(
      `v2/auto-scaling-groups/${id}/members`,
      requestConfig(regionId, projectTag),
    );
  }

  detachMember(id: number, memberId: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgMemberResDtoResponse>(
      `v2/auto-scaling-groups/${id}/members/${memberId}/detach`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }

  recreateMember(id: number, memberId: number, regionId: number, projectTag: string) {
    return this.api.post<IAsgMemberResDtoResponse>(
      `v2/auto-scaling-groups/${id}/members/${memberId}/recreate`,
      undefined,
      requestConfig(regionId, projectTag),
    );
  }
}

export default new AutoscalingGroupsService(apiClient);
